import { createApi, fakeBaseQuery, SkipToken } from "@reduxjs/toolkit/dist/query/react";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { db } from "@/firebase";
import { RootState } from "@/store/store";
import { pick } from "lodash";
import { Website } from "@/types";
import { ApiError } from "@/types/api";

interface OrganizationWebsiteQuery {
  organizationId: string | SkipToken;
  websiteId: string | SkipToken;
}

interface WebsiteUpdateMutation {
  organizationId: string | SkipToken;
  websiteId: string | SkipToken;
  website: Partial<Website>;
}

export const websiteApi = createApi({
  reducerPath: "website",
  baseQuery: fakeBaseQuery<ApiError>(),
  tagTypes: ["Website"],
  endpoints: (builder) => ({
    fetchOrganizationWebsite: builder.query<Website, OrganizationWebsiteQuery>({
      queryFn: async ({ organizationId, websiteId }) => {
        if (!organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);
        const snapshot = await getDoc(docRef);

        if (snapshot.exists()) {
          return {
            data: {
              ...snapshot.data(),
              id: snapshot.id,
            },
          };
        }

        return {
          error: {
            status: 404,
            statusText: "Sitio web no encontrado",
          },
        };
      },
      providesTags: ["Website"],
    }),
    updateWebsiteStyles: builder.mutation<void, WebsiteUpdateMutation>({
      queryFn: async ({ organizationId, websiteId, website }, { getState }) => {
        const store = getState() as RootState;

        const ALLOWED_FIELDS = ["styles"];

        if (!store?.auth?.user?.uid || !organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        try {
          const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);

          const payload = {
            ...pick(website, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(docRef, payload);
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al actualizar el sitio web",
            },
          };
        }
      },
      invalidatesTags: ["Website"],
    }),
    updateWebsite: builder.mutation<void, WebsiteUpdateMutation>({
      queryFn: async ({ organizationId, websiteId, website }, { getState }) => {
        const store = getState() as RootState;

        const ALLOWED_FIELDS = ["data", "lastUpdatedBy", "lastUpdatedAt"];

        if (!store?.auth?.user?.uid || !organizationId || !websiteId) {
          return {
            error: {
              status: 400,
              statusText: "Parámetros incorrectos",
            },
          };
        }

        try {
          const docRef = doc(db, `organizations/${String(organizationId)}/websites/${String(websiteId)}`);

          const payload = {
            ...pick(website, ALLOWED_FIELDS),
            lastUpdatedBy: store.auth.user.uid,
            lastUpdatedAt: Date.now(),
          };

          await updateDoc(docRef, payload);
        } catch {
          return {
            error: {
              status: 400,
              statusText: "Error al actualizar el sitio web",
            },
          };
        }
      },
      invalidatesTags: ["Website"],
    }),
  }),
});

export const { useUpdateWebsiteMutation, useUpdateWebsiteStylesMutation, useFetchOrganizationWebsiteQuery } = websiteApi;
